import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Instagram, MessageCircle, ArrowLeft, Download, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoursesPage() {
  const navigate = useNavigate();
  const [promoVideo, setPromoVideo] = useState({ video_base64: "" });
  const [courseVideos, setCourseVideos] = useState({ course1: "", course2: "", course3: "", course4: "" });
  const [classVideos, setClassVideos] = useState([]);
  const [batchImages, setBatchImages] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null); // Track which video is playing
  const scrollContainerRef = useRef(null);
  const batchScrollRef = useRef(null);
  const promoVideoRef = useRef(null);
  const courseVideoRefs = useRef({});

  useEffect(() => {
    fetchPromoVideo();
    fetchCourseVideos();
    fetchClassVideos();
    fetchBatchImages();
  }, []);

  const fetchPromoVideo = async () => {
    try {
      const response = await fetch(`${API}/course-promo-video`);
      const data = await response.json();
      setPromoVideo(data);
    } catch (error) {
      console.error("Error fetching promo video:", error);
    }
  };

  const fetchCourseVideos = async () => {
    try {
      const response = await fetch(`${API}/course-videos`);
      const data = await response.json();
      setCourseVideos(data);
    } catch (error) {
      console.error("Error fetching course videos:", error);
    }
  };

  const fetchClassVideos = async () => {
    try {
      const response = await fetch(`${API}/class-videos`);
      const data = await response.json();
      setClassVideos(data.videos || []);
    } catch (error) {
      console.error("Error fetching class videos:", error);
    }
  };

  const fetchBatchImages = async () => {
    try {
      const response = await fetch(`${API}/batch-images`);
      const data = await response.json();
      setBatchImages(data.images || []);
    } catch (error) {
      console.error("Error fetching batch images:", error);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      const newScroll = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  const scrollBatch = (direction) => {
    if (batchScrollRef.current) {
      const scrollAmount = 320;
      const newScroll = direction === 'left' 
        ? batchScrollRef.current.scrollLeft - scrollAmount
        : batchScrollRef.current.scrollLeft + scrollAmount;
      
      batchScrollRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  // Placeholder batch images if none uploaded
  const placeholderBatchImages = [
    { id: 'b1', title: 'Batch 2024 - December', placeholder: true },
    { id: 'b2', title: 'Batch 2024 - November', placeholder: true },
    { id: 'b3', title: 'Batch 2024 - October', placeholder: true },
    { id: 'b4', title: 'Batch 2024 - September', placeholder: true },
    { id: 'b5', title: 'Batch 2024 - August', placeholder: true },
  ];

  const displayBatchImages = batchImages.length > 0 ? batchImages : placeholderBatchImages;

  // Placeholder videos if none uploaded
  const placeholderClassVideos = [
    { id: 'p1', title: 'Makeup Class 1', placeholder: true },
    { id: 'p2', title: 'Hair Styling Class', placeholder: true },
    { id: 'p3', title: 'Bridal Makeup Demo', placeholder: true },
    { id: 'p4', title: 'Nail Art Tutorial', placeholder: true },
    { id: 'p5', title: 'Draping Techniques', placeholder: true },
    { id: 'p6', title: 'Advanced Contouring', placeholder: true },
  ];

  const displayClassVideos = classVideos.length > 0 ? classVideos : placeholderClassVideos;

  const [batchDates, setBatchDates] = useState({});

  useEffect(() => {
    fetchBatchDates();
  }, []);

  const fetchBatchDates = async () => {
    try {
      const response = await fetch(`${API}/course-batch-dates`);
      const data = await response.json();
      setBatchDates(data.batch_dates || {});
    } catch (error) {
      console.error("Error fetching batch dates:", error);
    }
  };

  const courses = [
    {
      id: 1,
      courseKey: "course1",
      name: "Course 1 : Pro. Makeup",
      duration: "30 days",
      fees: "₹28,000",
      description: "Master professional makeup techniques",
      highlights: ["Base Makeup", "Eye Makeup", "Contouring", "Bridal Makeup"],
      whatsappMessage: "I want to join Course 1 : Pro. Makeup"
    },
    {
      id: 2,
      courseKey: "course2",
      name: "Course 2 : Makeup & Hair",
      duration: "45 days",
      fees: "₹30,000",
      description: "Complete makeup and hair styling course",
      highlights: ["All Makeup", "Hair Styling", "Braiding", "Bridal Hair"],
      whatsappMessage: "I want to join Course 2 : Makeup & Hair"
    },
    {
      id: 3,
      courseKey: "course3",
      name: "Course 3 : Makeup, Hair, Nails or Draping",
      duration: "60 days",
      fees: "₹35,000",
      description: "Comprehensive beauty course with specialization",
      highlights: ["Makeup & Hair", "Nail Art OR Draping", "Portfolio Building"],
      whatsappMessage: "I want to join Course 3 : Makeup, Hair, Nails or Draping"
    },
    {
      id: 4,
      courseKey: "course4",
      name: "Course 4 : Makeup, Hair, Nails & Draping",
      duration: "60 days",
      fees: "₹39,500",
      description: "Complete professional makeup artist course",
      highlights: ["All Makeup", "Complete Hair", "Nail Art", "Saree Draping"],
      whatsappMessage: "I want to join Course 4 : Makeup, Hair, Nails & Draping"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            MUAS Academy
          </h1>
        </div>
      </header>

      {/* Course Promo Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto items-center">
            {/* Left Side - Text Content */}
            <div className="space-y-6">
              {/* Headings */}
              <h2 className="text-5xl md:text-6xl font-bold leading-none">
                <span className="text-black">Learn</span>{" "}
                <span className="text-pink-600">Pro MakeUp</span>
              </h2>
              <h3 className="text-3xl md:text-4xl font-bold text-black leading-none -mt-2">
                Start Earning
              </h3>
              <h3 className="text-3xl md:text-4xl font-bold text-black leading-none -mt-2">
                This Wedding Season
              </h3>

              {/* Mobile View - Video (between headings and paragraph) */}
              <div className="md:hidden">
                <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-pink-200 relative">
                  {promoVideo.video_base64 ? (
                    <>
                      <video
                        ref={promoVideoRef}
                        className="w-full h-auto"
                        src={promoVideo.video_base64}
                        playsInline
                        onEnded={() => setPlayingVideo(null)}
                        data-testid="promo-video-mobile"
                      />
                      {/* Play Button Overlay */}
                      {playingVideo !== 'promo-mobile' && (
                        <button
                          onClick={() => {
                            if (promoVideoRef.current) {
                              promoVideoRef.current.play();
                              setPlayingVideo('promo-mobile');
                            }
                          }}
                          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-all group"
                          data-testid="play-btn-promo-mobile"
                        >
                          <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                            <Play className="w-10 h-10 text-pink-600 ml-1" fill="currentColor" />
                          </div>
                        </button>
                      )}
                      {playingVideo === 'promo-mobile' && (
                        <button
                          onClick={() => {
                            if (promoVideoRef.current) {
                              promoVideoRef.current.pause();
                              setPlayingVideo(null);
                            }
                          }}
                          className="absolute inset-0"
                        />
                      )}
                    </>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-20 h-20 bg-white/80 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Play className="w-10 h-10 text-gray-400 ml-1" />
                        </div>
                        <p className="text-gray-500 font-semibold">Course Promo Video</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Paragraph */}
              <p className="text-pink-600 text-base leading-relaxed">
                Learn Pro Makeup From Aradhana Shreya & Team, also become Pro. in Hairstyling, Nail Extensions, Nail Art and Draping. The Best Makeup Course In Ranchi, as Reviewed by 100 of students across Jharkhand. Join the The Team Of Top Makeup Artists In Ranchi.
              </p>
            </div>

            {/* Right Side - Video (Desktop only) */}
            <div className="hidden md:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-pink-200 relative">
                {promoVideo.video_base64 ? (
                  <>
                    <video
                      ref={promoVideoRef}
                      className="w-full h-auto"
                      src={promoVideo.video_base64}
                      playsInline
                      onEnded={() => setPlayingVideo(null)}
                      data-testid="promo-video-desktop"
                    />
                    {/* Play Button Overlay */}
                    {playingVideo !== 'promo-desktop' && (
                      <button
                        onClick={() => {
                          if (promoVideoRef.current) {
                            promoVideoRef.current.play();
                            setPlayingVideo('promo-desktop');
                          }
                        }}
                        className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-all group"
                        data-testid="play-btn-promo-desktop"
                      >
                        <div className="w-24 h-24 bg-white/90 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                          <Play className="w-12 h-12 text-pink-600 ml-1" fill="currentColor" />
                        </div>
                      </button>
                    )}
                    {playingVideo === 'promo-desktop' && (
                      <button
                        onClick={() => {
                          if (promoVideoRef.current) {
                            promoVideoRef.current.pause();
                            setPlayingVideo(null);
                          }
                        }}
                        className="absolute inset-0"
                      />
                    )}
                  </>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-white/80 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Play className="w-12 h-12 text-gray-400 ml-1" />
                      </div>
                      <p className="text-gray-500 font-semibold">Course Promo Video</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Strip */}
      <section className="py-8 bg-gradient-to-r from-purple-50 to-pink-50 overflow-hidden">
        <div className="relative">
          <div className="flex animate-cert-scroll">
            {[...Array(4)].map((_, setIndex) => (
              <div key={setIndex} className="flex flex-nowrap">
                <div className="flex-shrink-0 w-32 h-24 mx-3 flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-2">
                  <img src="/certifications/msme 1.jpg" alt="MSME Registered" className="w-12 h-12 object-contain" />
                  <p className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">MSME Registered</p>
                </div>
                <div className="flex-shrink-0 w-32 h-24 mx-3 flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-2">
                  <img src="/certifications/ISO.jpg" alt="ISO Certified" className="w-12 h-12 object-contain" />
                  <p className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">ISO Certified</p>
                </div>
                <div className="flex-shrink-0 w-32 h-24 mx-3 flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-2">
                  <img src="/certifications/IAF.jpg" alt="IAF Certified" className="w-12 h-12 object-contain" />
                  <p className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">IAF Certified</p>
                </div>
                <div className="flex-shrink-0 w-32 h-24 mx-3 flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-2">
                  <img src="/certifications/lifetime support.jpg" alt="Lifetime Support" className="w-12 h-12 object-contain" />
                  <p className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">Lifetime Support</p>
                </div>
                <div className="flex-shrink-0 w-32 h-24 mx-3 flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-2">
                  <img src="/certifications/LEARNING-removebg-preview.png" alt="100% Learning Guarantee" className="w-12 h-12 object-contain" />
                  <p className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">100% Learning Guarantee</p>
                </div>
                <div className="flex-shrink-0 w-32 h-24 mx-3 flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-2">
                  <img src="/certifications/products provided.jpg" alt="Products Provided" className="w-12 h-12 object-contain" />
                  <p className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">Products Provided</p>
                </div>
                <div className="flex-shrink-0 w-32 h-24 mx-3 flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-2">
                  <img src="/certifications/free kit for home practice.png" alt="Free Kit For Practice" className="w-12 h-12 object-contain" />
                  <p className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">Free Kit For Practice</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes cert-scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .animate-cert-scroll {
            animation: cert-scroll 24s linear infinite;
          }
          @media (min-width: 768px) {
            .animate-cert-scroll {
              animation: cert-scroll 15s linear infinite;
            }
          }
          .animate-cert-scroll:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* Our Courses Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
          Our Courses
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          MUAS Academy Offers The Full Makeup Artist Course In Ranchi. Become The Best Makeup Artist In Jharkhand. Join Now
        </p>
      </section>

      {/* Courses Grid */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {courses.map((course) => (
            <Card key={course.id} className="border-2 border-purple-200 shadow-xl hover:shadow-2xl transition-all">
              <CardHeader className="pb-2">
                {/* Video Box */}
                <div className="rounded-2xl overflow-hidden mb-4 border-2 border-pink-200 relative">
                  <div className="aspect-video bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                    {courseVideos[course.courseKey] ? (
                      <>
                        <video
                          ref={(el) => courseVideoRefs.current[course.courseKey] = el}
                          className="w-full h-full object-cover"
                          src={courseVideos[course.courseKey]}
                          playsInline
                          onEnded={() => setPlayingVideo(null)}
                          data-testid={`course-video-${course.id}`}
                        />
                        {/* Play Button Overlay */}
                        {playingVideo !== course.courseKey && (
                          <button
                            onClick={() => {
                              const video = courseVideoRefs.current[course.courseKey];
                              if (video) {
                                video.play();
                                setPlayingVideo(course.courseKey);
                              }
                            }}
                            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-all group"
                            data-testid={`play-btn-course-${course.id}`}
                          >
                            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                              <Play className="w-8 h-8 text-purple-600 ml-1" fill="currentColor" />
                            </div>
                          </button>
                        )}
                        {/* Pause on click when playing */}
                        {playingVideo === course.courseKey && (
                          <button
                            onClick={() => {
                              const video = courseVideoRefs.current[course.courseKey];
                              if (video) {
                                video.pause();
                                setPlayingVideo(null);
                              }
                            }}
                            className="absolute inset-0"
                            data-testid={`pause-btn-course-${course.id}`}
                          />
                        )}
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Play className="w-8 h-8 text-gray-400 ml-1" />
                        </div>
                        <p className="text-gray-500 font-medium text-sm">Video Coming Soon</p>
                      </div>
                    )}
                  </div>
                </div>
                <CardTitle className="text-2xl text-left text-purple-700">{course.name}</CardTitle>
                <p className="text-left text-gray-600">{course.description}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-semibold text-purple-600">{course.duration}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Fees:</span>
                  <span className="font-semibold text-xl text-pink-600">{course.fees}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Next Batch starts:</span>
                  <span className="font-semibold text-green-600">
                    {batchDates[course.courseKey] || "Coming Soon"}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-gray-700">Included In Your Course</p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 flex items-center gap-1 text-xs"
                      onClick={() => alert(`Download syllabus for ${course.name}`)}
                    >
                      <Download className="w-4 h-4" />
                      Download Syllabus
                    </Button>
                  </div>
                  <ul className="space-y-1">
                    {course.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-purple-500">✓</span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button 
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                    onClick={() => navigate("/")}
                  >
                    Join Now
                  </Button>
                  <Button 
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold"
                    onClick={() => window.open(`https://wa.me/919773682082?text=${encodeURIComponent(course.whatsappMessage)}`)}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Contact Teacher
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Hostel & PG Strip */}
      <section className="py-6 bg-[#FFE0E6]">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              🏠 Hostel & PG Available for Students
            </p>
          </div>
        </div>
      </section>

      {/* Watch Class Videos Section */}
      <section className="py-16 bg-gradient-to-br from-pink-50 via-white to-purple-50 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Watch </span>
              <span className="text-black" style={{ fontSize: '90%' }}>Class Videos</span>
            </h2>
            <div className="w-32 h-1 bg-yellow-500 mx-auto mb-4"></div>
            <p className="text-2xl md:text-3xl font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Learn From Our Classes</p>
          </div>

          {/* Desktop View - Scrollable Row */}
          <div className="hidden md:block relative">
            <div
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {displayClassVideos.map((video) => (
                <div
                  key={video.id}
                  className="flex-shrink-0 w-80"
                >
                  <div className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105">
                    <div className="relative h-96 bg-gradient-to-br from-purple-100 to-pink-100">
                      {video.placeholder ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-8xl mb-4">🎥</div>
                            <p className="text-gray-600 font-semibold">Class Video</p>
                          </div>
                        </div>
                      ) : video.thumbnail_base64 ? (
                        <img
                          src={video.thumbnail_base64}
                          alt={video.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={video.video_base64}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-all">
                        <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                          <Play className="w-8 h-8 text-white ml-1 fill-white" />
                        </div>
                      </div>

                      {/* Video Title */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white font-bold text-center text-lg">{video.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {displayClassVideos.length > 4 && (
              <>
                <button
                  onClick={() => scroll('left')}
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-xl hover:from-pink-600 hover:to-purple-600 transition-all z-10"
                >
                  <ChevronLeft className="w-7 h-7 text-white" />
                </button>
                <button
                  onClick={() => scroll('right')}
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-xl hover:from-pink-600 hover:to-purple-600 transition-all z-10"
                >
                  <ChevronRight className="w-7 h-7 text-white" />
                </button>
              </>
            )}
          </div>

          {/* Mobile View - Carousel */}
          <div className="md:hidden">
            <div
              className="flex gap-4 overflow-x-auto px-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {displayClassVideos.map((video) => (
                <div
                  key={video.id}
                  className="flex-shrink-0 w-64"
                >
                  <div className="group relative overflow-hidden rounded-3xl shadow-2xl cursor-pointer" style={{ border: '3px solid #FCD34D' }}>
                    <div className="relative h-[450px] bg-gradient-to-br from-gray-800 to-gray-900">
                      {video.placeholder ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-7xl mb-4">🎥</div>
                            <p className="text-gray-400 font-semibold">Class Video</p>
                          </div>
                        </div>
                      ) : video.thumbnail_base64 ? (
                        <img
                          src={video.thumbnail_base64}
                          alt={video.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={video.video_base64}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                        <div className="w-20 h-20 rounded-full bg-yellow-500 flex items-center justify-center shadow-2xl">
                          <Play className="w-10 h-10 text-black ml-1 fill-black" />
                        </div>
                      </div>

                      {/* Video Title */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                        <p className="text-white font-bold text-center text-xl">{video.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hide scrollbar */}
        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </section>

      {/* Contact Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Your Journey?</h2>
          <p className="text-gray-600 mb-8">
            Contact us now to enroll in our courses or get more information
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
              onClick={() => window.open("tel:9773682082")}
            >
              <Phone className="w-5 h-5 mr-2" />
              Call Us: 9773682082
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-green-500 text-green-600 hover:bg-green-50"
              onClick={() => window.open("https://wa.me/919773682082?text=Hello, I'm interested in makeup courses")}
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-purple-500 text-purple-600 hover:bg-purple-50"
              onClick={() => window.open("https://www.instagram.com/aradhanashreyamakeup?igsh=eWhrMm44MnhrOG83")}
            >
              <Instagram className="w-5 h-5 mr-2" />
              Instagram
            </Button>
          </div>
        </div>
      </section>

      {/* Last 5 Batches Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Last 5 </span>
              <span className="text-black" style={{ fontSize: '90%' }}>Batches</span>
            </h2>
            <div className="w-32 h-1 bg-yellow-500 mx-auto mb-4"></div>
            <p className="text-2xl md:text-3xl font-semibold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Our Success Stories</p>
          </div>

          {/* Desktop View - Scrollable Row */}
          <div className="hidden md:block relative">
            <div
              ref={batchScrollRef}
              className="flex gap-6 overflow-x-auto pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {displayBatchImages.map((batch) => (
                <div
                  key={batch.id}
                  className="flex-shrink-0 w-72"
                >
                  <div className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105">
                    <div className="relative h-80 bg-gradient-to-br from-purple-100 to-pink-100">
                      {batch.placeholder ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-7xl mb-4">📸</div>
                            <p className="text-gray-600 font-semibold">Batch Photo</p>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={batch.image_base64}
                          alt={batch.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}

                      {/* Batch Title */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white font-bold text-center text-lg">{batch.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {displayBatchImages.length > 4 && (
              <>
                <button
                  onClick={() => scrollBatch('left')}
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-xl hover:from-purple-600 hover:to-pink-600 transition-all z-10"
                >
                  <ChevronLeft className="w-7 h-7 text-white" />
                </button>
                <button
                  onClick={() => scrollBatch('right')}
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-xl hover:from-purple-600 hover:to-pink-600 transition-all z-10"
                >
                  <ChevronRight className="w-7 h-7 text-white" />
                </button>
              </>
            )}
          </div>

          {/* Mobile View - Carousel */}
          <div className="md:hidden">
            <div
              className="flex gap-4 overflow-x-auto px-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {displayBatchImages.map((batch) => (
                <div
                  key={batch.id}
                  className="flex-shrink-0 w-64"
                >
                  <div className="group relative overflow-hidden rounded-3xl shadow-2xl cursor-pointer" style={{ border: '3px solid #A855F7' }}>
                    <div className="relative h-80 bg-gradient-to-br from-purple-100 to-pink-100">
                      {batch.placeholder ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-6xl mb-4">📸</div>
                            <p className="text-gray-500 font-semibold">Batch Photo</p>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={batch.image_base64}
                          alt={batch.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}

                      {/* Batch Title */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white font-bold text-center text-lg">{batch.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Location Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            Our Location
          </h2>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto items-center">
            {/* Left Side - Address Text */}
            <div className="space-y-4">
              <p className="text-black text-xl">
                We are located at
              </p>
              <h3 className="text-black text-4xl md:text-5xl font-black leading-tight">
                Sahajanand Chowk, Harmu, Ranchi
              </h3>
              <div className="pt-4">
                <p className="text-gray-600 text-sm underline mb-2">
                  Address
                </p>
                <p className="text-gray-700 text-base">
                  C-127, Harmu Housing Colony, Harmu Ranchi 834002
                </p>
              </div>
            </div>

            {/* Right Side - Google Map (Desktop) */}
            <div className="hidden md:block rounded-2xl overflow-hidden shadow-2xl h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3663.0779413928234!2d85.30622931496908!3d23.344135484786746!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f4e1e5e5e5e5e5%3A0x573656250116139851!2sMUAS%20Academy!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MUAS Academy Location"
              ></iframe>
            </div>

            {/* Mobile View - CTA Button */}
            <div className="md:hidden text-center">
              <Button
                size="lg"
                className="bg-black hover:bg-gray-800 text-white font-bold text-lg px-12 py-6 rounded-full shadow-xl"
                onClick={() => window.open("https://share.google/mAeU7ULFywbXvrOfK", "_blank")}
              >
                Check Google Location
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Google Logo */}
            <div className="flex justify-center mb-6">
              <div className="flex items-center gap-1 text-4xl font-bold">
                <span className="text-blue-500">G</span>
                <span className="text-red-500">o</span>
                <span className="text-yellow-500">o</span>
                <span className="text-blue-500">g</span>
                <span className="text-green-500">l</span>
                <span className="text-red-500">e</span>
                <span className="text-gray-700 ml-2">Reviews</span>
              </div>
            </div>

            {/* Stars */}
            <div className="flex justify-center mb-6">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-10 h-10 text-yellow-400 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
            </div>

            {/* Rating Text */}
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800">
              Rated 5.0 by Our Students
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              See what our students say about MUAS Academy on Google
            </p>

            {/* CTA Button */}
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold text-lg px-12 py-6 rounded-full shadow-xl"
              onClick={() => window.open("https://www.google.com/search?sca_esv=6a0b219675808712&rlz=1C1ONGR_en-GBIN1070IN1070&sxsrf=ANbL-n5ssCYbkbqkwZX1QI6stmvJWok7kg:1769505963848&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOYz_MKRqqJ643fA0uLbs59Gfmm4ueIenQ6nreo-z6_xq5rbWJveiQpwGr7IwAIucQ-tdVHgItpLG9XbdjpQU5qJ4NgZ6&q=MUAS+Academy+Reviews&sa=X&ved=2ahUKEwiNsJyzs6uSAxU2xjgGHS6GKV4Q0bkNegQIKhAH&biw=1280&bih=551&dpr=1.5&aic=0", "_blank")}
            >
              Read Our Google Reviews
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
